import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { History, Search } from 'lucide-react';

import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TableSkeleton } from '@/components/skeletons';
import { changelogService, type ChangelogListItem } from '@/services/changelog';

const COLUMN_COUNT = 3;

/**
 * Absolute, not relative: "4 weeks ago" does not tell the reader when a change
 * went live, which is the one thing this column is for.
 */
const formatDate = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });

const ChangelogList = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState<ChangelogListItem[] | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    changelogService.list().then((data) => {
      if (!cancelled) setEntries(data);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (entries ?? []).filter((e) => q === '' || e.title.toLowerCase().includes(q));
  }, [entries, search]);

  const isLoading = entries === null;

  return (
    <div className="space-y-4 sm:space-y-6 w-full max-w-[1310px] mx-auto px-4 sm:px-0">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">
            Changelog{' '}
            {entries !== null && (
              <span className="text-muted-foreground font-normal">({entries.length})</span>
            )}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Every change that can move a number, and what happened after it
          </p>
        </div>
      </div>

      <div className="rounded-lg border bg-card">
        {/* Toolbar */}
        <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between border-b">
          <div className="relative w-full sm:max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search changes..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={`pl-9${isLoading ? ' opacity-50 cursor-not-allowed' : ''}`}
              readOnly={isLoading}
            />
          </div>

          {/* The area filter is hidden with the area column; both come back
              together if areas earn their place again. */}
        </div>

        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="w-full">Change</TableHead>
              <TableHead className="w-[300px]">Notes</TableHead>
              <TableHead className="text-center w-[140px] whitespace-nowrap">Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className={isLoading ? 'pointer-events-none' : ''}>
            {isLoading ? (
              <TableSkeleton columns={COLUMN_COUNT} rows={5} />
            ) : visible.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} className="h-48 text-center text-muted-foreground">
                  <div className="flex flex-col items-center justify-center">
                    <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
                      <History className="h-6 w-6 text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-medium mb-2">No changes found</h3>
                    <p className="text-sm text-muted-foreground">
                      No changes match your current filters.
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              visible.map((entry) => (
                <TableRow
                  key={entry.slug}
                  className="cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => navigate(`/changelog/${entry.slug}`)}
                >
                  <TableCell className="font-medium w-full">{entry.title}</TableCell>
                  <TableCell className="w-[300px] text-xs text-muted-foreground">
                    <span className="line-clamp-2">{entry.notes ?? ''}</span>
                  </TableCell>
                  <TableCell className="text-center w-[140px] whitespace-nowrap text-muted-foreground tabular-nums">
                    {formatDate(entry.date)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default ChangelogList;
